/**
 * The single source of truth for which benefits the beta home may show.
 *
 * Both surfaces that render the page — the beta branch of `/dashboard` and the
 * full-page `/reimagined/home-2026` — build their flags here. They used to
 * duplicate the getter, which is how the nav rail and the page body were able
 * to disagree about whether a benefit existed.
 *
 * Two independent things gate a benefit, and BOTH must hold:
 *
 *   1. **Partner entitlement** — does this company buy the product?
 *      (`partner.companySettings`, or the centralised service flag.)
 *   2. **Demo scenario** — does the scenario being demoed include it?
 *      (`reimagined-data`'s `showTuitionAssistance` / `showStudentLoanRepayment`
 *      / `showPslf`, which fold the scenario in already.)
 *
 * The partner-only switches (LPN, mentoring, SYF, IDR) had no scenario half, so
 * a `slrp-only` demo could still surface TA mentoring and a `ta-only` demo could
 * still surface loan-derived IDR. Each is paired with its parent benefit below.
 */
import type { BetaHomeFlags } from 'tio-employee/utils/beta/home-model';
import type FeaturesService from 'tio-common/services/features';
import type PartnerService from 'tio-employee/services/partner';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';

export function betaHomeFlags(
  partner: PartnerService,
  reimaginedData: ReimaginedDataService,
  features: FeaturesService
): BetaHomeFlags {
  const settings = partner.companySettings;

  // These already combine partner config with the demo scenario.
  const showTuitionAssistance = reimaginedData.showTuitionAssistance;
  const showStudentLoanRepayment = reimaginedData.showStudentLoanRepayment;
  const showPslf = reimaginedData.showPslf;

  return {
    showTuitionAssistance,
    showStudentLoanRepayment,
    showPslf,

    /**
     * A standalone entitlement — `partner.hasWellness` in live mode, and the
     * built-in `wellness` demo scenario turns it on with every other benefit
     * OFF. So it must not be derived from loans: doing that hides the only
     * wellness destination in the scenario that exists to showcase it.
     */
    showFinancialWellness: reimaginedData.showFinancialWellness,

    // Learning Partner Network is a tuition benefit — discounted study.
    showLpn: showTuitionAssistance && (settings?.lpn?.isEnabled ?? false),

    // TA mentoring is configured under tuitionAssistance and is meaningless
    // without it.
    showMentoring:
      showTuitionAssistance && (settings?.tuitionAssistance?.isTaMentoringEnabled ?? false),

    // Save Your Future matches 401(k) against qualifying loan payments, so it
    // depends on the loan-repayment benefit.
    showSyf: showStudentLoanRepayment && (settings?.modules?.syf?.isEnabled ?? false),

    // Income-driven repayment is a loan repayment plan.
    showIdr: showStudentLoanRepayment && partner.isIdrEnabled,

    /**
     * Rides the overlay's surface rather than adding a sixth: the two coexist by
     * design and a company that wants one wants the other.
     */
    showPhoneCapture: features.can('phoneCapture.surfaces.postLoginOverlay'),
  };
}
